/**
 * @file VatForm.tsx
 *
 * The VAT & taxes form, mirroring WSB-v2: a breadcrumb header + a single
 * {@link ContentContainer} card with a VAT status radio group (Active/Inactive),
 * the Standard VAT rate field (a "%" addon, disabled while VAT is inactive), and
 * an "Apply VAT to shipping" toggle with a sub-caption; a Back / Save footer.
 * Owns the editable state via {@link useVatFormState}, validates the rate on save,
 * and reports the fields to its parent.
 */

import { useId, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { ContentContainer, GeneralField, Icon, Radio, Toggle } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { FormFooter } from '../../components/FormFooter';
import { FieldShell } from '../PaymentFormField';
import { useVatFormState } from './use-vat-form-state';
import type { VatFormFields } from './vat-form-types';
import styles from '../payments.module.css';

interface VatFormProps {
  /** The seed fields (stable reference; the hook re-seeds when it changes). */
  seed: VatFormFields;
  saving: boolean;
  saveError?: string;
  onSubmit: (fields: VatFormFields) => void;
  onCancel: () => void;
}

/**
 * Renders the VAT & taxes form.
 *
 * @param props - Seed fields, save state, and the submit/cancel handlers.
 *
 * @returns The VAT & taxes form.
 */
export function VatForm({ seed, saving, saveError, onSubmit, onCancel }: VatFormProps) {
  const intl = useIntl();
  const baseId = useId();
  const { fields, patch, errors, validate, firstErrorRef } = useVatFormState(seed);

  const id = (suffix: string): string => `${baseId}-${suffix}`;

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(fields);
  };

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/payments">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...adminMessages.paymentsVatTitle} />
        </h2>
        <div className="gv-text-sm">
          <FormattedMessage {...adminMessages.paymentsVatDescription} />
        </div>
      </header>

      <div className={`gv-flex gv-flex-col gv-gap-lg gv-w-full ${styles.column}`}>
        <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
          <div className="gv-form-option gv-w-full">
            <span className="gv-label">
              <FormattedMessage {...adminMessages.paymentsVatStatusLabel} />
            </span>
            <div className="gv-flex gv-flex-col gv-gap-md">
              <label
                className="gv-flex gv-items-center gv-gap-sm"
                data-testid="vat-status-active"
              >
                <Radio
                  groupName="vat-status"
                  checked={fields.enabled}
                  condensed
                  onChange={() => patch({ enabled: true })}
                />
                <span>
                  <FormattedMessage {...adminMessages.paymentsStatusActive} />
                </span>
              </label>
              <label
                className="gv-flex gv-items-center gv-gap-sm"
                data-testid="vat-status-inactive"
              >
                <Radio
                  groupName="vat-status"
                  checked={!fields.enabled}
                  condensed
                  onChange={() => patch({ enabled: false })}
                />
                <span>
                  <FormattedMessage {...adminMessages.paymentsStatusInactive} />
                </span>
              </label>
            </div>
          </div>

          <FieldShell
            id={id('rate')}
            label={intl.formatMessage(adminMessages.paymentsVatRateLabel)}
          >
            <GeneralField
              id={id('rate')}
              inputRef={firstErrorRef as RefObject<HTMLInputElement>}
              value={fields.standardRate}
              error={errors.standardRateError}
              addon="%"
              disabled={!fields.enabled}
              data-testid="vat-standard-rate"
              aria-label={intl.formatMessage(adminMessages.paymentsVatRateLabel)}
              onChange={(event) => patch({ standardRate: event.target.value })}
            />
          </FieldShell>

          <div
            className="gv-flex gv-flex-row gv-items-start gv-gap-sm"
            data-testid="vat-apply-shipping"
          >
            <Toggle
              checked={fields.applyToShipping}
              condensed
              disabled={!fields.enabled}
              onChange={(checked) => patch({ applyToShipping: checked })}
            />
            <div className="gv-flex gv-flex-col gv-gap-xs">
              <span className="gv-text-sm">
                <FormattedMessage {...adminMessages.paymentsVatApplyShippingLabel} />
              </span>
              <span className="gv-caption-lg gv-text-on-alternative">
                <FormattedMessage {...adminMessages.paymentsVatApplyShippingDescription} />
              </span>
            </div>
          </div>
        </ContentContainer>

        {saveError ? (
          <p className="gv-text-sm gv-state-critical" role="alert" data-testid="vat-error">
            {saveError}
          </p>
        ) : null}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="vat-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            <FormattedMessage {...adminMessages.formBack} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="vat-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.paymentsSaving} />
            ) : (
              <FormattedMessage {...adminMessages.paymentsSave} />
            )}
          </button>
        </FormFooter>
      </div>
    </div>
  );
}
